import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./PropertySearch.css";
import RangeInputs from "../../components/RangeInputs";
import { useLocation } from "react-router-dom";
import { NOT_FOUND_M, NOT_FOUND_G, PROPERTY_SEARCH as columns } from "../../utils/column_maps";
import { containsExcludedWord, getOwnerColor, getPhoneTypeLabel, getPropertyTypeLabel, hasPermission, propertyTypeLabels, updateLocalStorageRolling } from "../../utils/common";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuth } from "../../context/AuthProvider";
import { ArrowBigRightDashIcon, Copy, CopyCheck, Trash2 } from "lucide-react";
import ReasonsConfirmModal from "../../components/ReasonsConfirmModal";
import ConfirmModal from "../../components/ConfirmModal";
import axiosInstance from "../../api/axiosInstance";

const PropertySearch = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [properties, setProperties] = useState([]);
  const location = useLocation();
  const [refLead, setRefLead] = useState(null);
  const [refLeadType, setRefLeadType] = useState(null);
  const [DMLeadLoading, setDMLeadLoading] = useState(new Set());
  const [DMLeadData, setDMLeadData] = useState({});

  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  // eslint-disable-next-line
  const [copiedAddresses, setCopiedAddresses] = useState([]);
  const [visitedLinks, setVisitedLinks] = useState([]);
  const [dealMachineLoaded, setDealMachineLoaded] = useState([]);
  const getItemIdentifier = (item) => item.apn || item.id;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    address: "",
    city: "",
    county: "",
    zip: "",
    state: "",
    property_type: "",
    astix: "",
    beds: [null, null],
    baths: [null, null],
    year_build: [null, null],
    building_area: [null, null],
    lot_area: [null, null],
    lot_size_acres: [null, null],
    ownership_length_in_years: [null, null],
    latitude: [null, null],
    longitude: [null, null],
  });

  const [copied, setCopied] = useState({ key: "", id: null });

  useEffect(() => {
    const storedVisited = JSON.parse(localStorage.getItem("visitedLinks") || "[]");
    const storedCopied = JSON.parse(localStorage.getItem("copiedAddresses") || "[]");
    const storedDealMachine = JSON.parse(localStorage.getItem("dealMachineLoaded" || "[]"));
    setVisitedLinks(storedVisited);
    setCopiedAddresses(storedCopied);
    setDealMachineLoaded(storedDealMachine);
  }, []);

  const handleCopy = (key, value, id) => {
    if (!value) return;
    navigator.clipboard.writeText(value);

    const updatedCopied = updateLocalStorageRolling("copiedAddresses", id, 50);
    setCopiedAddresses(updatedCopied);

    // const updatedCopyEvents = updateLocalStorageRolling("copyEvents", id, 50);

    setCopied({ key, id });
    setTimeout(() => setCopied({ key: "", id: null }), 1500);
  };

  const handleDealMachineLoad = (apn) => {
    const updatedDealMachine = updateLocalStorageRolling("dealMachineLoaded", apn, 50);
    setDealMachineLoaded(updatedDealMachine);
  };

  const isDealMachineLoaded = (apn) => {
    return dealMachineLoaded?.includes(apn);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const leadTypes = ["lead-m", "lead-s", "lead-d", "lead-b", "lead-g"];
    const leadType = leadTypes.find((type) => params.get(type)) || null;

    const parseRange = (param, plus_minus = 0, integerOnly = false) => {
      const value = params.get(param);
      if (!value) return [];

      const extractNumber = (str) => {
        const match = str.match(integerOnly ? /\d+/ : /[\d.]+/);
        return match ? Number(match[0]) : null;
      };

      let parts = [];

      if (value.includes(",")) {
        parts = value
          .split(",")
          .map(extractNumber)
          .filter((v) => v !== null && v !== 0 && v !== undefined);
      } else if (value.includes("-")) {
        parts = value
          .split("-")
          .map(extractNumber)
          .filter((v) => v !== null && v !== 0 && v !== undefined);
      } else {
        const num = extractNumber(value);
        if (num !== null && num !== 0 && num !== undefined) parts = [num];
      }

      if (parts.length === 1) {
        const num = parts[0];
        const start = Math.max(0, Math.floor(num - plus_minus));
        const end = Math.floor(num + plus_minus);
        return [start, end].filter((v) => v !== 0 && v !== null && v !== undefined);
      }

      return parts;
    };

    // Extract all parameters first
    const zip = params.get("zipcode") || "";

    // If zip exists, skip filling city, county, state
    const prefilled = {
      astix: params.get("address") || "",
      city: zip ? "" : params.get("city") || "",
      county: zip ? "" : params.get("county") || "",
      zip,
      state: zip ? "" : params.get("state") || "",
      beds: parseRange("beds"),
      baths: parseRange("baths"),
      year_build: parseRange("year_build"),
      building_area: parseRange("building_area"),
      ownership_length_in_years: parseRange("ownership_legth"),
      lot_area: leadType === "lead-s" ? parseRange("lot_area", 1) : parseRange("lot_area"),
      // lot_size_acres: parseDecimalRange("lot_size_acres", 100),
    };

    const fetchCases = async (id) => {
      try {
        const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/duval_properties/cases?case_id=${id}&parties=true`);
        const data = await resp.data.data[0];
        return data;
      } catch (err) {
        console.error("Failed to fetch leads", err);
      }
    };

    if (leadType) {
      const identifier = params.get(leadType);
      (async () => {
        if (leadType === "lead-d") {
          const refLead = await fetchCases(identifier);
          setRefLead(refLead);
          setRefLeadType(leadType);
        } else {
          const refLead = await fetchLeadData({ identifier, type: leadType });
          setRefLead(refLead);
          setRefLeadType(leadType);
        }
      })();
    }

    setFormData((prev) => ({ ...prev, ...prefilled }));
  }, [location.search]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const hasAtLeastOneValue = Object.values(formData).some((v) => v && (Array.isArray(v) ? v.some((val) => val !== null && !isNaN(val)) : v.toString().trim() !== ""));

    if (!hasAtLeastOneValue) {
      toast.error("Please fill at least one field.");
      return;
    }

    if (formData.astix && (!formData.zip || formData.zip.toString().trim() === "")) {
      toast.error("ZIP code is required when using Astix search.");
      return;
    }

    // Validation for ranges: To >= From or reset To to null
    const rangeFields = ["beds", "baths", "year_build", "building_area", "lot_area"];
    rangeFields.forEach((field) => {
      const [from, to] = formData[field];
      if (from !== null && to !== null && to < from) {
        toast.warning(`"${field}" To value is less than From. Resetting To to null.`);
        formData[field][1] = null;
      }
    });

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            const [from, to] = value;
            if (from !== null || to !== null) {
              // Always send both values, use null if empty
              queryParams.append(key, `${from ?? "null"},${to ?? "null"}`);
            }
          } else if (value.toString().trim() !== "") {
            queryParams.append(key, value);
          }
        }
      });

      const url = `${process.env.REACT_APP_BASE_URL}api/propwire_properties/property?${queryParams.toString()}`;
      const res = await axiosInstance.get(url);
      const data = await res.data;
      const slugify = (str) => str?.trim().replace(/\s+/g, "-").toLowerCase() || "";
      const makeFpsLink = (addr, zip, city, state) => (addr && zip && city && state ? `https://www.fastpeoplesearch.com/address/${slugify(addr)}_${slugify(city)}-${slugify(state)}-${zip}` : null);

      const transformed = (data?.data || []).map((exist) => ({
        ...exist,
        fps_link: makeFpsLink(exist.address, exist.zip, exist.city, exist.state),
        fps_link_mailing: makeFpsLink(exist.owner_mailing_address, exist.owner_mailing_zip),
      }));

      setProperties(transformed);
      if (data?.data?.length > 0) {
        toast.success(`${data?.data?.length} properties found successfully`);
      } else {
        toast.error(`No Property Find.`);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch data.");
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const textFields = [
    { name: "first_name", placeholder: "First Name..." },
    { name: "last_name", placeholder: "Last Name..." },
    { name: "astix", placeholder: "Address..." },
    { name: "city", placeholder: "City..." },
    { name: "county", placeholder: "County..." },
    { name: "zip", placeholder: "ZIP..." },
    { name: "state", placeholder: "State..." },
  ];

  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  const fetchLeadData = async ({ identifier, type }) => {
    if (!identifier) return;
    try {
      let url = `${process.env.REACT_APP_BASE_URL}api/ispeed_properties/${type}?identifier=${identifier}`;
      if (type === "lead-g") {
        url = `${process.env.REACT_APP_BASE_URL}api/gs/${identifier}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error("❌ Failed to fetch leads");

      const lead = await res.json();
      let cleaned = null;
      if (type === "lead-m") {
        cleaned = (() => {
          const obj = { ...lead };
          NOT_FOUND_M.forEach(({ key }) => {
            if (!(key in obj)) {
              obj[key] = "";
            }
          });
          return obj;
        })();
      } else if (type === "lead-g") {
        cleaned = (() => {
          const obj = { ...lead.data };
          NOT_FOUND_G.forEach(({ key }) => {
            if (!(key in obj)) {
              obj[key] = "";
            }
          });
          return obj;
        })();
      } else {
        cleaned = (() => {
          const obj = { ...lead };
          NOT_FOUND_M.forEach(({ key }) => {
            if (!(key in obj)) {
              obj[key] = "";
            }
          });
          return obj;
        })();
      }
      return cleaned;
    } catch (err) {
      console.error("Error fetching leads:", err);
      return null;
    }
  };

  const findDealMachine = async (address, apn) => {
    try {
      setDMLeadLoading((prev) => new Set(prev).add(apn));

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/enrichment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addresses: [address] }),
      });

      const result = await response.json();
      setDMLeadData((prev) => ({ ...prev, [apn]: result }));

      handleDealMachineLoad(apn);
    } catch (err) {
      setDMLeadData((prev) => ({ ...prev, [apn]: { success: false, message: err.message } }));
    } finally {
      setDMLeadLoading((prev) => {
        const next = new Set(prev);
        next.delete(apn);
        return next;
      });
    }
  };

  const visibleColumns = refLead ? columns : columns.filter((col) => col.key !== "deal_machine");

  const handleOpenMoveModal = (row) => {
    setSelectedRow(row);
    setMoveModalOpen(true);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteLead = async (identifier, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}api/ispeed_properties/${refLeadType}/${identifier}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ lead_status: status }),
      });

      if (!res.ok) throw new Error("❌ Failed to Delete lead");

      toast.success("Lead Deleted successfully.");
      window.close();
    } catch (err) {
      console.error("Error updating lead status:", err);
      toast.error(err.message || "Error Deleting Lead.");
    }
  };

  const OwnersList = ({ property, refLead, item }) => {
    const owners = property?.contacts || [];
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedPhones, setSelectedPhones] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);

    if (owners.length === 0) return <div>No owners found</div>;

    const ownerKey = (owner, idx) => owner.dm_person_id || `owner-${idx}`;
    const isOwnerSelected = (owner, idx) => selectedOwner && ownerKey(selectedOwner.owner, selectedOwner.idx) === ownerKey(owner, idx);

    const toggleOwner = (owner, idx) => {
      if (isOwnerSelected(owner, idx)) {
        setSelectedOwner(null);
        setSelectedPhones([]);
        setSelectedEmail(null);
        return;
      }
      setSelectedOwner({ owner, idx });
      setSelectedPhones([]);
      setSelectedEmail(null);
    };

    const togglePhone = (owner, idx, phone) => {
      if (!isOwnerSelected(owner, idx)) {
        setSelectedOwner({ owner, idx });
        setSelectedPhones([phone]);
        setSelectedEmail(null);
      } else {
        setSelectedPhones((prev) => (prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone]));
      }
    };

    const toggleEmail = (owner, idx, email) => {
      if (!isOwnerSelected(owner, idx)) {
        setSelectedOwner({ owner, idx });
        setSelectedPhones([]);
        setSelectedEmail(email);
      } else {
        setSelectedEmail((prev) => (prev?.address === email.address ? null : email));
      }
    };

    const confirmSelection = () => {
      const selectedLead = {
        apn: item.apn,
        refLeadId: refLead.id,
        refLeadIdentifier: refLeadType === "lead-m" ? refLead?.identifier : refLeadType === "lead-b" ? refLead?.id : refLeadType === "lead-s" ? refLead?.id : null,
        refLeadType: refLeadType,

        address: `${property.address}, ${property.city}, ${property.state}, ${property.zip}`,
        owners: [
          {
            firstName: selectedOwner?.owner.first_name,
            lastName: selectedOwner?.owner.last_name,
            email: selectedEmail?.address,
            phones: selectedPhones.map((p) => (p.type ? `${p.number} ${getPhoneTypeLabel(p.type)}` : p.number)).join("\n"),
          },
        ],
      };

      const params = new URLSearchParams({
        lead: JSON.stringify(selectedLead),
        page: "1",
      });

      window.open(`/added-leads?${params.toString()}`, "_blank");
    };

    return (
      <div className="!space-y-2 text-left">
        {owners.map((owner, idx) => (
          <div key={ownerKey(owner, idx)} className="">
            <label className="font-semibold flex item-center !mb-1 cursor-pointer" style={{ color: getOwnerColor(idx) }}>
              <span onClick={() => toggleOwner(owner, idx)} className="flex items-center">
                <input type="checkbox" className="!w-4 !h-4 accent-teal-600 !mr-2 cursor-pointer" checked={!!isOwnerSelected(owner, idx)} onChange={() => toggleOwner(owner, idx)} />
                {owner.full_name || `${owner.first_name || ""} ${owner.last_name || ""}`.trim()}
              </span>
            </label>

            {/* Phones */}
            {owner.phones?.length > 0 ? (
              <ul className="!mt-1 list-inside text-sm text-gray-600">
                {owner.phones.map((phone, pIdx) => (
                  <li
                    key={`${phone.number}-${pIdx}`}
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePhone(owner, idx, phone);
                    }}
                  >
                    <input type="checkbox" className="!w-4 !h-4 accent-teal-600 pointer-events-none" checked={!!isOwnerSelected(owner, idx) && selectedPhones.includes(phone)} readOnly />
                    <span>
                      {phone?.number}
                      {phone?.type && <> - {getPhoneTypeLabel(phone.type)}</>}
                      {phone?.do_not_call && <span className="text-red-500 font-medium"> (DNC)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="!ml-4 text-red-400">phones not found</div>
            )}

            {/* Emails */}
            {owner.emails?.length > 0 ? (
              <ul className="!mt-1 list-inside text-sm text-gray-600">
                {owner.emails.map((email, eIdx) => (
                  <li
                    key={`${email.address}-${eIdx}`}
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEmail(owner, idx, email);
                    }}
                  >
                    <input type="checkbox" className="!w-4 !h-4 accent-teal-600 pointer-events-none" checked={!!isOwnerSelected(owner, idx) && selectedEmail?.address === email.address} readOnly />
                    <span>{email.address}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="!ml-4 text-red-400">emails not found</div>
            )}
          </div>
        ))}

        {/* Single Confirm button at bottom */}
        {selectedOwner && (
          <div className="!mt-3 flex justify-center">
            <button onClick={confirmSelection} className="!px-3 !py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer">
              Add Lead
            </button>
          </div>
        )}
      </div>
    );
  };

  const addAddress = ({ refLead, item }) => {
    const selectedLead = {
      apn: item.apn,
      refLeadId: refLead.id,
      refLeadIdentifier:
        refLeadType === "lead-m"
          ? refLead?.identifier //
          : refLeadType === "lead-b"
            ? refLead?.id
            : refLeadType === "lead-s"
              ? refLead?.id
              : refLeadType === "lead-g"
                ? refLead?.id
                : null,
      refLeadType: refLeadType,
      address: item.label,
      owners: [
        {
          firstName: "",
          lastName: "",
          email: "",
          phones: "",
        },
      ],
    };

    const params = new URLSearchParams({
      lead: JSON.stringify(selectedLead),
      page: "1",
    });

    window.open(`/added-leads?${params.toString()}`, "_blank");
  };

  return (
    <>
      <form className="input_fields_container SP_search_container !grid md:!grid-cols-3 gap-2 sm:gap-4 md:gap-6" onSubmit={handleSubmit}>
        {textFields.map(({ name, placeholder }) => (
          <div key={name} className="relative w-full flex item-center">
            <input key={name} type="text" name={name} className="input_field" placeholder={placeholder} value={formData[name]} onChange={(e) => handleChange(name, e.target.value)} />

            {/* {formData[name] && (
              <button type="button" onClick={() => handleChange(name, "")} className="absolute right-2 top-5/12 -translate-y-1/2 text-red-500 hover:text-red-700 whitespace-nowrap cursor-pointer bg-gray-200/70 hover:bg-gray-200/100 rounded-full !p-1">
                <X className="" />
              </button>
            )} */}
          </div>
        ))}

        <RangeInputs label="Beds" value={formData.beds} onChange={(val) => handleChange("beds", val)} />
        <RangeInputs label="Baths" value={formData.baths} onChange={(val) => handleChange("baths", val)} />

        <RangeInputs label="Year Built" value={formData.year_build} onChange={(val) => handleChange("year_build", val)} />
        <RangeInputs label="Building Sqft" value={formData.building_area} onChange={(val) => handleChange("building_area", val)} />
        <RangeInputs label="Lot Sqft" value={formData.lot_area} onChange={(val) => handleChange("lot_area", val)} />
        <RangeInputs label="Lot Acres" value={formData.lot_size_acres} onChange={(val) => handleChange("lot_size_acres", val)} />
        <RangeInputs label="Latitude" value={formData.latitude} onChange={(val) => handleChange("latitude", val)} />
        <RangeInputs label="Longitude" value={formData.longitude} onChange={(val) => handleChange("longitude", val)} />
        <RangeInputs label="Owner Ship (years)" value={formData.ownership_length_in_years} onChange={(val) => handleChange("ownership_length_in_years", val)} />

        <div className="input_field">
          <select name="property_type" className={`cursor-pointer w-full focus:!outline-none ${formData.property_type ? "text-gray-700" : "text-gray-500"}`} value={formData.property_type || ""} onChange={(e) => handleChange("property_type", e.target.value)}>
            <option value="">-- Select Property Type --</option>
            {Object.entries(propertyTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3 flex justify-center item-center">
          <button className="input_button" type="submit" disabled={loading}>
            {loading ? "Searching..." : "Continue"}
          </button>
        </div>
        {error && <div style={{ color: "red", gridColumn: "1 / -1" }}>{error}</div>}
        {refLead && (
          <div className="w-full flex justify-center items-center sm:col-span-2 md:col-span-3">
            <div className="flex !justify-center !item-center !pr-4 !space-x-1">
              {hasPermission("not-found-list-1", user) && (
                <>
                  <div className="">
                    <button type="button" onClick={() => handleOpenMoveModal(refLead)} className="!text-blue-600 hover:!text-blue-800 !flex !items-center !justify-center cursor-pointer bg-slate-100/70 hover:bg-slate-100/100 rounded-full w-8 h-8" title="Move Lead">
                      <ArrowBigRightDashIcon size={18} />
                    </button>
                  </div>
                  <div className="">
                    <button type="button" onClick={() => handleOpenDeleteModal(refLead)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center cursor-pointer bg-slate-100/70 hover:bg-slate-100/100 rounded-full w-8 h-8" title="Delete Lead">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </>
              )}

              {!hasPermission("not-found-list-1", user) && hasPermission("not-found-list-2", user) && (
                <div className="">
                  <button type="button" onClick={() => handleOpenDeleteModal(refLead)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center cursor-pointer bg-slate-100/70 hover:bg-slate-100/100 rounded-full w-8 h-8" title="Delete Lead">
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
            {refLeadType === "lead-m" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 w-full">
                {[
                  { label: "Address", key: "address" },
                  { label: "Name", key: "name" },
                  { label: "Email", key: "email" },
                  { label: "Phone", key: "phone" },
                  { label: "Home Type", key: "property_type" },
                  { label: "Beds", key: "beds" },
                  { label: "Baths", key: "baths" },
                  { label: "Date", key: "received" },
                  { label: "City", key: "city" },
                  { label: "County", key: "county" },
                  { label: "State", key: "state" },
                  { label: "Zip", key: "zipcode" },
                ].map((field) => (
                  <p key={field.key} className="text-left w-full">
                    <span className="font-semibold">{field.label}:</span> <span>{refLead[field.key]}</span>
                  </p>
                ))}
              </div>
            ) : refLeadType === "lead-b" ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full">
                {[
                  { label: "City", key: "city" },
                  { label: "County", key: "county" },
                  { label: "State", key: "state" },
                  { label: "Home Type", key: "property_type" },
                  { label: "Beds", key: "beds" },
                  { label: "Baths", key: "baths" },
                  { label: "Building Sqft", key: "building_sqft" },
                  { label: "Lot Sqft", key: "lot_land_sqft" },
                ].map((field) => (
                  <p key={field.key} className="text-left w-full">
                    <span className="font-semibold">{field.label}:</span> <span>{refLead[field.key]}</span>
                  </p>
                ))}
              </div>
            ) : refLeadType === "lead-s" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 w-full">
                {[
                  { label: "Address", key: "label", col_span: 2 },
                  { label: "Latitude", key: "latitude", col_span: 1 },
                  { label: "Longitude", key: "longitude", col_span: 1 },
                  { label: "Zip", key: "zip_code", col_span: 1 },
                  { label: "Home Type", key: "property_type", col_span: 1 },
                  { label: "Sq ft", key: "square_footage", col_span: 1 },
                  { label: "Build year", key: "year_of_construction", col_span: 1 },
                  { label: "Beds", key: "bedrooms", col_span: 1 },
                  { label: "Baths", key: "bathrooms", col_span: 1 },
                  { label: "Lot Size Acres", key: "lot_size_acres", col_span: 1 },
                  { label: "Owner Ship", key: "ownership_duration", col_span: 1 },
                  { label: "Buyers in City", key: "buyers_in_lead_city", col_span: 1 },
                  { label: "Buyers in County", key: "buyers_in_lead_county", col_span: 1 },
                  { label: "Lead Source", key: "provider", col_span: 2 },
                  { label: "Motivational Factors", key: "seller_motivation", col_span: 4 },
                ].map((field) => (
                  <p key={field.key} className={`text-left w-full md:col-span-${field.col_span}`}>
                    <span className="font-semibold">{field.label}:</span> <span>{refLead[field.key]}</span>
                  </p>
                ))}
              </div>
            ) : refLeadType === "lead-d" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 w-full">
                {[
                  { label: "Address", key: "address", col_span: 2 },
                  { label: "Type", key: "case_type", col_span: 2 },
                  { label: "File Date", key: "file_date", col_span: 1 },
                  { label: "Case Number", key: "case_number", col_span: 1 },
                  { label: "Parties", key: "parties", col_span: 2 },
                ].map((field) => (
                  <p key={field.key} className={`text-left w-full md:col-span-${field.col_span} space-x-2`}>
                    {field.key !== "parties" && <span className="font-semibold">{field.label}: </span>}
                    <span>
                      {field.key !== "parties"
                        ? refLead[field.key]
                        : (() => {
                            const matchedParties = refLead.parties?.filter((p) => {
                              // const partyAddress = "";
                              // const caseStreet = "";
                              const partyAddress = p.address?.toLowerCase() || "";
                              const caseStreet = refLead.street?.toLowerCase() || "";

                              return partyAddress.includes(caseStreet) && !containsExcludedWord(p.name);
                            });

                            const plaintiffs = matchedParties?.filter((p) => p.party_type === "PLAINTIFF");
                            const defendants = matchedParties?.filter((p) => p.party_type === "DEFENDANT");

                            return (
                              <div className="flex">
                                {plaintiffs?.length > 0 && (
                                  <div className="flex space-x-4">
                                    <div className="font-semibold">Plaintiffs:</div>
                                    <div className=" felx flex-col">
                                      {plaintiffs.map((p, i) => (
                                        <div key={`pl-${i}`}>
                                          {p.name} {p.is_primary ? "(Primary)" : "(Secondary)"}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {defendants?.length > 0 && (
                                  <div className="flex space-x-4">
                                    <div className="font-semibold">Defendants:</div>
                                    <div className=" felx flex-col">
                                      {defendants.map((p, i) => (
                                        <div key={`df-${i}`}>
                                          {p.name} {p.is_primary ? "(Primary)" : "(Secondary)"}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {!plaintiffs?.length && !defendants?.length && "—"}
                              </div>
                            );
                          })()}
                    </span>
                  </p>
                ))}
              </div>
            ) : refLeadType === "lead-g" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 w-full">
                {[
                  { label: "Address", key: "address" },
                  { label: "Name", key: "first_name" },
                  { label: "Email", key: "email" },
                  { label: "Phone", key: "phone_number" },
                  { label: "Home Type", key: "property_type" },
                  { label: "Beds", key: "bedrooms" },
                  { label: "Baths", key: "bathrooms" },
                  { label: "Year Built", key: "year_built" },
                  { label: "Lot Size", key: "lot_size" },
                  { label: "City", key: "city" },
                  { label: "County", key: "county" },
                  { label: "State", key: "state" },
                  { label: "Zip", key: "zipcode" },
                  { label: "Occupancy", key: "occupancy" },
                  { label: "Motivation", key: "motivation" },
                  { label: "Valuation", key: "valuation" },
                ].map((field) => (
                  <p key={field.key} className="text-left w-full">
                    <span className="font-semibold">{field.label}:</span> <span>{refLead[field.key]}</span>
                  </p>
                ))}
              </div>
            ) : (
              <></>
            )}{" "}
          </div>
        )}
      </form>

      <div>
        {loading ? (
          <div className="!p-4 !text-left !text-gray-500 w-full">
            <div className="w-full max-w-screen text-center">
              <LoadingSpinner size={30} textSize={20} />
            </div>
          </div>
        ) : properties.length > 0 ? (
          <div className="mt-6 !w-full !overflow-x-auto">
            <table className="!border !border-gray-300 !table-auto" style={{ width: `${totalTableWidth}px`, minWidth: "97%" }}>
              <thead>
                <tr className="!bg-gray-200">
                  {/* {refLead && (
                    <>
                      {hasPermission("not-found-list-1", user) && (
                        <>
                          <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                          <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                        </>
                      )}

                      {!hasPermission("not-found-list-1", user) && hasPermission("not-found-list-2", user) && <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>}
                    </>
                  )} */}
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="!border !border-gray-300 !px-4 !py-2" style={{ minWidth: `${col.width}px`, width: `${col.width}px`, textAlign: col.headerAlign }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {properties.length > 0 ? (
                  properties.map((item, idx) => (
                    <tr key={item.id || idx} className="!hover:bg-gray-100">
                      {visibleColumns.map((col) => {
                        const value = (item?.[col.key] ?? "").toString().trim();

                        return (
                          <td
                            key={col.key}
                            className="truncate !border !border-gray-300 !px-2 !py-1 text-sm"
                            title={value}
                            style={{
                              minWidth: `${col.width}px`,
                              width: `${col.width}px`,
                              textAlign: col.cellAlign,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {col.key === "address" ? (
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="flex item-center">
                                    <div className="flex justify-center item-center space-x-2">
                                      <div title={item.label}>A: {item.label || "N/A"}</div>
                                    </div>
                                    {item.label && item.label !== "null" && item.fps_link && (
                                      <div title={item.label} className="!ml-2 flex !space-x-2">
                                        <a
                                          title={item.label}
                                          href={item.fps_link}
                                          onClick={() => {
                                            const linkId = item.apn || item.id;
                                            const updated = updateLocalStorageRolling("visitedLinks", linkId);
                                            setVisitedLinks(updated);
                                          }}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`!ml-2 text-nowrap ${visitedLinks.includes(item.apn || item.id) ? "!text-purple-800 text-medium" : "!text-blue-600 hover:!text-blue-800"}`}
                                        >
                                          {"[ FPS ]"}
                                        </a>
                                        <div title={item.label} className={`flex justify-center items-center !w-6 !h-6 rounded-full cursor-pointer ${copiedAddresses.includes(item.apn || item.id) ? "bg-green-600 text-white" : "bg-gray-200/70 hover:bg-gray-200/100 text-black"}`} onClick={() => handleCopy("label", item.label, item.apn)}>
                                          {copied.key === "label" && copied.id === item.apn ? <CopyCheck className="!w-4 !h-4 text-black" /> : <Copy className="!w-4 !h-4" />}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex item-center">
                                    <div title={item.owner_mailing_label}>M: {item.owner_mailing_label || "N/A"}</div>
                                    {item.owner_mailing_label && item.owner_mailing_label !== "null" && item.fps_link_mailing && (
                                      <div title={item.owner_mailing_label} className="!ml-2 flex !space-x-2">
                                        <a
                                          href={item.fps_link_mailing}
                                          onClick={() => {
                                            const linkId = getItemIdentifier(item) + "-mailing";
                                            const updated = updateLocalStorageRolling("visitedLinks", linkId);
                                            setVisitedLinks(updated);
                                          }}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`!text-blue-600 hover:!text-blue-800 text-nowrap ${visitedLinks.includes(getItemIdentifier(item) + "-mailing") ? "!text-purple-800 text-medium visited:!text-purple-800 text-medium" : "!text-blue-600 hover:!text-blue-800"}`}
                                        >
                                          {"[ FPS ]"}
                                        </a>
                                        <div title={item.owner_mailing_label} className={`flex justify-center items-center !w-6 !h-6 rounded-full cursor-pointer ${copiedAddresses.includes(getItemIdentifier(item) + "-mailing") ? "bg-green-600 text-white" : "bg-gray-200/70 hover:bg-gray-200/100 text-black"}`} onClick={() => handleCopy("owner_mailing_label", item.owner_mailing_label, getItemIdentifier(item) + "-mailing")}>
                                          {copied.key === "owner_mailing_label" && copied.id === item.apn ? <CopyCheck className="!w-4 !h-4 text-black" /> : <Copy className="!w-4 !h-4" />}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {refLead && (
                                  <div className="flex item-center !space-x-2">
                                    <button title={item.label} type="button" onClick={() => addAddress({ refLead: refLead, item: item })} className="!px-3 !py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:bg-gray-400 cursor-pointer text-nowrap">
                                      Add Address
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : col.key === "owner_names" ? (
                              <>
                                {item.ownership_length_in_years > 0 && (
                                  <div>
                                    <span>
                                      <span className="font-medium">Duration: </span>
                                      {item.ownership_length_in_years} - years
                                    </span>
                                  </div>
                                )}

                                <div>
                                  <div className="font-medium">Owner{value.length > 1 ? "s" : ""}: </div>
                                  {value.split(",").map((owner, i) => (
                                    <div key={i}>{owner.trim()}</div>
                                  ))}
                                </div>
                              </>
                            ) : col.key === "deal_machine" ? (
                              <span className="!ml-2">
                                {DMLeadData[item.apn] ? (
                                  (() => {
                                    const result = DMLeadData[item.apn];
                                    const property = result?.data?.[0];

                                    if (property?.matched) {
                                      return <OwnersList property={property} refLead={refLead} item={item} />;
                                    }
                                    return <div className="text-red-600">{property?.match_failure?.reason || result?.message || "Property not found in Deal Machine."}</div>;
                                  })()
                                ) : (
                                  <button onClick={() => findDealMachine(item.label, item.apn)} disabled={DMLeadLoading.has(item.apn)} className={`!px-2 !py-1 rounded-sm cursor-pointer !text-white ${DMLeadLoading.has(item.apn) ? "!bg-gray-400 cursor-not-allowed" : isDealMachineLoaded(item.apn) ? "!bg-green-600 hover:!bg-green-700" : "!bg-[#008080] hover:!bg-[#007070]"}`}>
                                    {DMLeadLoading.has(item.apn) ? (
                                      <div className="flex items-center">
                                        <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                        </svg>
                                        Loading...
                                      </div>
                                    ) : (
                                      "Load Deal Machine"
                                    )}
                                  </button>
                                )}
                              </span>
                            ) : col.key === "property_type" ? (
                              getPropertyTypeLabel(value)
                            ) : col.key === "fps_link" && value ? (
                              <a href={value} target="_blank" rel="noopener noreferrer" className="!text-blue-600 !underline hover:!text-blue-800">
                                Check Details
                              </a>
                            ) : col.key === "lot_size" ? (
                              <div>
                                <div>
                                  <span>Acres: {item.lot_size_acres || "N/A"}</span>
                                </div>
                                <div>
                                  <span>Sq Ft: {item.lot_size_sf || "N/A"}</span>
                                </div>
                              </div>
                            ) : col.key === "building" ? (
                              <div>
                                <div>
                                  <span>
                                    <span className="font-medium">Type: </span>
                                    {getPropertyTypeLabel(item.property_type) || "N/A"}
                                  </span>
                                </div>
                                <div>
                                  <span>
                                    <span className="font-medium">Build: </span>
                                    {item.year_built || "N/A"}
                                  </span>
                                </div>
                                <div>
                                  <span>
                                    <span className="font-medium">Size: </span>
                                    {item.building_area_sf || "N/A"}
                                  </span>
                                </div>
                                <div>
                                  <span>
                                    <span className="font-medium">Latitude: </span>
                                    {item.latitude || "N/A"}
                                  </span>
                                </div>
                                <div>
                                  <span>
                                    <span className="font-medium">Longitude: </span>
                                    {item.longitude || "N/A"}
                                  </span>
                                </div>
                              </div>
                            ) : col.key === "beds_baths" ? (
                              <div>
                                <div>
                                  <span>
                                    <span className="font-medium">Beds: </span>
                                    {item.bedrooms || "N/A"}
                                  </span>
                                </div>
                                <div>
                                  <span>
                                    <span className="font-medium">Baths: </span>
                                    {item.bathrooms
                                      ? Number.isInteger(Number(item.bathrooms))
                                        ? parseInt(item.bathrooms, 10) // show whole number
                                        : `${Math.floor(item.bathrooms)}-${Math.ceil(item.bathrooms)}` // show range
                                      : "N/A"}
                                  </span>
                                </div>
                              </div>
                            ) : // : col.key === "ownership_length_in_years" ? (
                            //   <div>
                            //     <div>
                            //       <span>{item.ownership_length_in_years ? `${item.ownership_length_in_years} - years` : "N/A"}</span>
                            //     </div>
                            //   </div>
                            // )
                            col.key === "equity_details" ? (
                              <div>
                                <div className="flex flex-col item-center">
                                  <span>
                                    <span className="font-medium">Estimated Equity: </span>
                                    {item.estimated_equity ? `${item.estimated_equity}` : "N/A"}
                                  </span>
                                  <span>
                                    <span className="font-medium">Estimated Equity Percentage: </span>
                                    {item.estimated_equity_percentage ? `${item.estimated_equity_percentage}` : "N/A"}
                                  </span>
                                  <span>
                                    <span className="font-medium">Estimated Value: </span>
                                    {item.estimated_value ? `${item.estimated_value}` : "N/A"}
                                  </span>
                                  <span>
                                    <span className="font-medium">Estimated Price/sf: </span>
                                    {item.estimated_price_per_sf ? `${item.estimated_price_per_sf}` : "N/A"}
                                  </span>
                                  <span>
                                    <span className="font-medium">Estimated Zip Price/sf: </span>
                                    {item.estimated_zip_price_per_sf ? `${item.estimated_zip_price_per_sf}` : "N/A"}
                                  </span>
                                </div>
                              </div>
                            ) : col.key === "foreclosure_details" ? (
                              <div>
                                <div className="flex flex-col item-center">
                                  <span>
                                    <span className="font-medium">Foreclosure Type: </span>
                                    {item.foreclosure_type ? item.foreclosure_type.replace(/^"|"$/g, "") : "N/A"}
                                  </span>
                                  <span>
                                    <span className="font-medium">Notice Type: </span>
                                    {item.foreclosure_notice_type ? item.foreclosure_notice_type.replace(/^"|"$/g, "") : "N/A"}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              value || "N/A"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length} className="!border !border-gray-300 !px-4 !py-2 !text-center">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="SP_table_no_data">No data found.</p>
        )}
      </div>

      <ReasonsConfirmModal
        isOpen={moveModalOpen} //
        setMoveModalOpen={setMoveModalOpen}
        onCancel={() => setMoveModalOpen(false)}
        selectedRow={selectedRow}
        identifier={refLeadType === "lead-b" ? selectedRow?.id : refLeadType === "lead-g" ? selectedRow?.id : selectedRow?.identifier}
        source={refLeadType === "lead-b" ? "b" : refLeadType === "lead-g" ? "g" : refLeadType === "lead-s" ? "s" : "m"}
        fetchData={false}
        shouldCloseTab={true}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        title="Confirm Delete" //
        message={`Are you sure you want to delete this lead?`}
        isOpen={deleteModalOpen}
        onConfirmTitle="Delete"
        onConfirm={() => {
          if (selectedRow) {
            handleDeleteLead(refLeadType === "lead-b" ? selectedRow.id : refLeadType === "lead-g" ? selectedRow.id : selectedRow?.identifier, 1);
          }
          setDeleteModalOpen(false);
        }}
        onCancelTitle="Cancel"
        onCancel={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default PropertySearch;
